import React, { useRef } from 'react';
import { useSource } from '../../contexts/SourceContext';
import { sourceConfigs } from './source_configs';
import Markdown from '../ui-common/markdown';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Video, Bot } from "lucide-react";

import { SourceMarker } from './source_marker';

// Truncate to maxLen characters without splitting an open LaTeX math delimiter ($...$).
// A plain substring cut can leave an unclosed "$", which remark-math silently ignores,
// causing raw LaTeX to appear as literal text in the collapsed preview.
export function truncateLatexSafe(text, maxLen = 100) {
  const slice = text.substring(0, maxLen);
  const dollarCount = (slice.match(/\$/g) || []).length;
  // Odd count means we cut inside a $...$ pair — back up to before the opening "$"
  if (dollarCount % 2 !== 0) {
    const cutPoint = slice.lastIndexOf('$');
    return text.substring(0, cutPoint);
  }
  return slice;
}

const SourceListItem = ({ source, sourceNumber, isOpen, onClick, messageId }) => {
  const { activeSource, hoveredSource, setHoveredSource } = useSource();
  const itemRef = useRef(null);

  const sourceConfig = sourceConfigs[source.src_group] || sourceConfigs.moodle;

  const sourceText = sourceConfig.make_text(source);
  let truncatedText = sourceText;

  if (!isOpen) {
    if (source.src_group === 'not_from_context') {
      truncatedText = sourceText;
    } else {
      const firstLine = sourceText.split('\n')[0];
      if (sourceText.length > 100 || sourceText.includes('\n')) {
          truncatedText = `"${truncateLatexSafe(firstLine)}..."`;
      } else {
          truncatedText = `"${firstLine}"`;
      }
    }
  } else {
    if (source.src_group === 'not_from_context') {
      truncatedText = sourceText;
    } else {
      truncatedText = `"${sourceText}"`;
    }
  }

  const isActive = activeSource && activeSource.messageId === messageId && activeSource.number === sourceNumber;

  const toggleExpand = () => {
    onClick(sourceNumber); // Use the onClick prop to toggle the source in ChatResponse's state
  };

  const handleMouseEnter = () => {
    setHoveredSource({ messageId: messageId, number: sourceNumber });
  };

  const handleMouseLeave = () => {
    setHoveredSource(null);
  };

  const popoverHeader = (
    <div className="flex items-start gap-2">
      {/* {sourceConfig.icon} */}
      <div className="flex flex-col flex-1 w-0">
        <span className="text-sm font-bold truncate text-foreground">{sourceConfig.make_title(source)}</span>
        <div className="text-xs text-muted-foreground">
          <span>{sourceConfig.make_metadata(source)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <li
      ref={itemRef}
      className={cn(
        'p-2 border rounded-md source-list-item',
        isActive ? 'bg-primary/10 border-transparent' : 'bg-muted'
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center gap-2 cursor-pointer" onClick={toggleExpand}>
        <SourceMarker messageId={messageId} sourceNumber={sourceNumber} onClick={onClick} isOpen={isOpen} isNotInContext={source.src_group === 'not_from_context'} />
        {source.src_group === 'lectures' ? (
          <Video className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : source.src_group === 'not_from_context' ? null : (
          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
        <div className="flex-1">
            <Markdown className="text-xs">{truncatedText}</Markdown>
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className={cn("mt-2 pt-2 ml-8 overflow-hidden", source.src_group !== 'not_from_context' && "border-t border-gray-300 dark:border-border")}
          >
            {sourceConfig.make_link_component(source, popoverHeader)}
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
};

export { SourceListItem };
